import { useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Pencil, Layers } from "lucide-react";
import type { ProjectContext } from "@iamu/shared";

export interface ArchitectureSectionProps {
  context: ProjectContext;
  onUpdate: (field: string, value: unknown) => void;
  canEdit: boolean;
}

export function ArchitectureSection({
  context,
  onUpdate,
  canEdit,
}: ArchitectureSectionProps) {
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [archValue, setArchValue] = useState(context.architecture ?? "");
  const [notesValue, setNotesValue] = useState(context.notes ?? "");
  const footerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setArchValue(context.architecture ?? "");
    setNotesValue(context.notes ?? "");
  }, [context.architecture, context.notes]);

  useEffect(() => {
    if (!isEditingNotes) return;
    const rafId = requestAnimationFrame(() => {
      footerRef.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "nearest",
      });
    });
    return () => cancelAnimationFrame(rafId);
  }, [isEditingNotes]);

  const hasChanges =
    archValue.trim() !== (context.architecture ?? "").trim() ||
    notesValue.trim() !== (context.notes ?? "").trim();

  function handleSaveNotes() {
    onUpdate("architecture", archValue.trim() || undefined);
    onUpdate("notes", notesValue.trim() || undefined);
    setIsEditingNotes(false);
  }

  function handleCancelNotes() {
    setArchValue(context.architecture ?? "");
    setNotesValue(context.notes ?? "");
    setIsEditingNotes(false);
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Layers className="h-4 w-4 text-primary" />
              <CardTitle className="text-base">
                Architecture & technical notes
              </CardTitle>
            </div>
            <CardDescription className="mt-1">
              Technical context that helps walkthrough runners reason about
              implementation constraints, stack choices, and deployment
              topology.
            </CardDescription>
          </div>
          {canEdit && !isEditingNotes && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditingNotes(true)}
            >
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              Edit notes
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isEditingNotes ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="architecture-input">Architecture summary</Label>
              <Input
                id="architecture-input"
                value={archValue}
                onChange={(e) => setArchValue(e.target.value)}
                placeholder="e.g. Event-sourced modular monolith (Go), multi-tenant, RLS-enforced"
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                Key architectural decisions — language, style, data model
                approach, deployment topology.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes-input">Additional engineering notes</Label>
              <Textarea
                id="notes-input"
                value={notesValue}
                onChange={(e) => setNotesValue(e.target.value)}
                placeholder="Anything else runners should know — team constraints, migration plans, known debt..."
                rows={3}
              />
            </div>
            <div ref={footerRef} className="flex items-center gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={handleCancelNotes}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={!hasChanges}
                onClick={handleSaveNotes}
              >
                Save technical notes
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground">
                Architecture summary
              </span>
              <p className="text-sm font-medium text-foreground">
                {context.architecture ? (
                  context.architecture
                ) : (
                  <span className="text-muted-foreground font-normal">
                    No architecture summary specified.
                  </span>
                )}
              </p>
            </div>
            <div className="space-y-1 border-t border-border/50 pt-3">
              <span className="text-xs font-medium text-muted-foreground">
                Additional engineering notes
              </span>
              <p className="text-sm text-foreground whitespace-pre-wrap">
                {context.notes ? (
                  context.notes
                ) : (
                  <span className="text-muted-foreground">
                    No additional engineering notes provided.
                  </span>
                )}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
